import type { IChat } from '@/modules/chat-matrix-fast/types/IChat'
import { getMatrixSession } from '@/modules/chat-matrix-fast/matrixSession'

const SNAPSHOT_STORAGE_PREFIX = 'chat-matrix-fast:list-snapshot-v1:'
const MAX_CHATS = 150
const SNAPSHOT_TTL_MS = 7 * 24 * 60 * 60_000

type Snapshot = {
  at: number
  chats: IChat[]
}

function storageKey(): string | null {
  const userId = getMatrixSession()?.userId?.trim()
  return userId ? SNAPSHOT_STORAGE_PREFIX + userId : null
}

function slimChat(chat: IChat): IChat {
  const { topics: _topics, ...rest } = chat
  return {
    ...rest,
    members: Array.isArray(chat.members) ? chat.members.slice(0, 10) : [],
    transition_status: undefined,
    online: undefined,
  }
}

export function loadChatListSnapshot(): IChat[] | null {
  if (typeof localStorage === 'undefined') {
    return null
  }

  const key = storageKey()
  if (!key) {
    return null
  }

  try {
    const raw = localStorage.getItem(key)
    if (!raw) {
      return null
    }

    const parsed = JSON.parse(raw) as Snapshot
    if (!parsed || !Array.isArray(parsed.chats) || !parsed.chats.length) {
      return null
    }
    if (Date.now() - (parsed.at ?? 0) > SNAPSHOT_TTL_MS) {
      localStorage.removeItem(key)
      return null
    }

    return parsed.chats
  } catch {
    return null
  }
}

export function persistChatListSnapshot(chats: IChat[]): void {
  if (typeof localStorage === 'undefined') {
    return
  }

  const key = storageKey()
  if (!key) {
    return
  }

  try {
    const snapshot: Snapshot = {
      at: Date.now(),
      chats: chats.slice(0, MAX_CHATS).map(slimChat),
    }
    localStorage.setItem(key, JSON.stringify(snapshot))
  } catch {
    // quota / private mode
  }
}

export function clearChatListSnapshot(): void {
  if (typeof localStorage === 'undefined') {
    return
  }

  const key = storageKey()
  if (key) {
    localStorage.removeItem(key)
  }
}
